import 'react-big-calendar/lib/sass/styles.scss'
import 'react-big-calendar/lib/addons/dragAndDrop/styles.scss'
import React, { Fragment, memo } from 'react'
import moment, { Moment } from 'moment'
import { Calendar, momentLocalizer, View } from 'react-big-calendar'
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop'
import { BookingPaidStatus } from 'entities/bookings/model/dtoTypes'
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react'

import { EVENT_THEME, WrapperCalendar } from './style'

export interface CalendarEvent {
    allDay?: boolean
    id?: string
    bookingId: number | null
    orderId: number | null
    counterpartyName: string | null
    counterpartyPhone: string | null
    comment: string | null
    isCompany: boolean
    title: React.ReactNode | null
    start: Date | null
    end: Date | null
    resourceId: number | null
    resourceName: string
    isNew: boolean
    isEdit: boolean
    isBlocked?: boolean
    isBlockedByAreaPolicy?: boolean
    paidStatus?: BookingPaidStatus
}

// TODO: fix types
interface IPropsReactBigCalendar {
    views: any
    defaultView: View
    min?: Date
    max?: Date
    currentDate: Moment
    onDateChange: (date: Moment) => void
    events?: CalendarEvent[] | undefined
    resources?: { id: number; title: string }[]
    components?: any
    onEventResize?: (props: any) => void
    onDropSlot?: (props: any) => void
    onSelectSlot?: (props: any) => void
    onClickEvent?: (props: any) => void
    onShowMore?: (props: any) => void
    onKeyPress?: (event: any, e: any) => void
    backgroundEvents?: CalendarEvent[] | undefined
    blockingEvents?: CalendarEvent[] | undefined
    onDoubleClickEvent?: (props: any) => void
}

const DnDCalendar = withDragAndDrop(Calendar)
const localizer = momentLocalizer(moment)

const tooltipStringGeneration = (event: any) =>
    `ФИО: ${event.counterpartyName}\nТелефон: ${event.counterpartyPhone}\nЗал: ${event.resourceName}\nНачало: ${moment(event.start).format('DD.MM.YYYY HH:mm')}\nКонец: ${moment(event.end).format('DD.MM.YYYY HH:mm')}`

const eventPropGetter = (event: CalendarEvent) => {
    if (event.isBlockedByAreaPolicy) {
        return {
            className: 'blocked-by-area-policy',
            name: event.id,
        }
    }

    if (event.isBlocked) {
        return {
            className: 'blocked',
            name: event.id,
        }
    }

    if (event.paidStatus) {
        if (event.paidStatus === 'paid') {
            return {
                style: EVENT_THEME.DEFAULT,
                // currently classnames used for element selection
                className: event.id,
                name: event.id,
            }
        }
        if (event.paidStatus === 'unpaid') {
            return {
                style: EVENT_THEME.UNPAID,
                className: event.id,
                name: event.id,
            }
        }
        if (event.paidStatus === 'partially_paid') {
            return {
                style: EVENT_THEME.PARTIALLY_PAID,
                className: event.id,
                name: event.id,
            }
        }
    }

    if (event.isEdit) {
        return {
            style: EVENT_THEME.EDIT,
            className: event.id,
            name: event.id,
        }
    }

    if (event.isNew) {
        return {
            style: EVENT_THEME.NEW,
            className: event.id,
            name: event.id,
        }
    }

    return {
        style: EVENT_THEME.DEFAULT,
        className: event.id,
        name: event.id,
    }
}

const ReactBigCalendarComponent = ({
    views,
    defaultView,
    min,
    max,
    currentDate,
    onDateChange,
    events,
    resources,
    components,
    onDropSlot,
    onEventResize,
    onSelectSlot,
    onClickEvent,
    onShowMore,
    onKeyPress,
    onDoubleClickEvent,
    backgroundEvents,
    blockingEvents,
}: IPropsReactBigCalendar) => {
    const event = ({ event }: { event: CalendarEvent }) => {
        if (event.isBlocked) {
            return <span style={{ fontSize: 12 }}>{event.title ?? ''}</span>
        }

        if (defaultView === 'month') {
            if (event.resourceName) {
                return (
                    <span style={{ fontSize: 12 }}>
                        {event.resourceName ?? ''}
                    </span>
                )
            }
            return null
        }

        return (
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 10,
                    paddingTop: 10,
                }}
            >
                {event.counterpartyName ? (
                    <span style={{ fontSize: 12 }}>
                        {event.counterpartyName ?? ''}
                    </span>
                ) : (
                    <></>
                )}
                {event.counterpartyPhone ? (
                    <Menu>
                        <MenuButton as={Fragment}>
                            <span style={{ fontSize: 12 }}>
                                {event.counterpartyPhone ?? ''}
                            </span>
                        </MenuButton>
                        <MenuItems anchor='bottom' className='dropDownCall'>
                            <MenuItem as='div'>
                                <a
                                    target='_blank'
                                    rel='noreferrer'
                                    href={`tel:${event.counterpartyPhone}`}
                                >
                                    Позвонить
                                </a>
                            </MenuItem>
                            <MenuItem as='div'>
                                <a
                                    target='_blank'
                                    rel='noreferrer'
                                    href={`https://wa.me/${event.counterpartyPhone.replace(/[^\d+]/g, '')}`}
                                >
                                    WhatsApp
                                </a>
                            </MenuItem>
                            <MenuItem as='div'>
                                <a
                                    target='_blank'
                                    rel='noreferrer'
                                    href={`https://t.me/+${event.counterpartyPhone.replace(/[^\d]/g, '')}`}
                                >
                                    Telegram
                                </a>
                            </MenuItem>
                            <MenuItem as='div'>
                                <div
                                    className='dropDownCall__copy'
                                    onClick={() => {
                                        navigator.clipboard.writeText(
                                            event.counterpartyPhone
                                                ? event.counterpartyPhone
                                                : '',
                                        )
                                    }}
                                >
                                    Копировать
                                </div>
                            </MenuItem>
                        </MenuItems>
                    </Menu>
                ) : (
                    <></>
                )}
                {event.comment && event.comment?.length ? (
                    <span style={{ fontSize: 12 }}>{event.comment ?? ''}</span>
                ) : (
                    <></>
                )}
            </div>
        )
    }

    const tooltipAccessor =
        defaultView === 'month' ? tooltipStringGeneration : null

    const eventTimeRangeFormat = (date: { start: Date; end: Date }) => {
        const end = moment(date.end)
        const endDateFormatted =
            end.hour() === 23 && end.minute() === 59
                ? end.add(1, 'minutes').format('HH:mm')
                : end.format('HH:mm')
        return `${moment(date.start).format('HH:mm')} - ${endDateFormatted}`
    }

    return (
        <WrapperCalendar>
            <DnDCalendar
                onKeyPressEvent={onKeyPress}
                onDragStart={onClickEvent}
                onEventResize={onEventResize}
                onEventDrop={onDropSlot}
                onSelectSlot={onSelectSlot}
                onDoubleClickEvent={onDoubleClickEvent}
                localizer={localizer}
                views={views}
                defaultView={defaultView}
                timeslots={1}
                step={60}
                min={min ?? moment().startOf('day').toDate()}
                max={max ?? moment().endOf('day').toDate()}
                date={currentDate.toDate()}
                onNavigate={date => onDateChange(moment(date))}
                toolbar={false}
                events={events}
                resources={resources}
                eventPropGetter={e => eventPropGetter(e as any)}
                components={{
                    event,
                    ...components,
                }}
                tooltipAccessor={tooltipAccessor}
                enableAutoScroll={false}
                selectable
                onShowMore={onShowMore}
                messages={{
                    showMore: (total: number) => `еще ${total}`,
                }}
                formats={{
                    eventTimeRangeFormat,
                }}
                backgroundEvents={[
                    ...(backgroundEvents ?? []),
                    ...(blockingEvents ?? []),
                ]}
            />
        </WrapperCalendar>
    )
}

export const ReactBigCalendar = memo(ReactBigCalendarComponent)
